import Option, { type OptionElement, type TOptionProps, type TOptionRef } from '@atlas/uicorn/lib/Control/Option';
import PaintIcon from '@atlas/uicorn/lib/Icon/PaintIcon';
import type { TExample } from '@atlas/uicorn/preview/modules';
import * as React from 'react';
import styles from './IconButton.module.css';
import cn from 'classnames';

const IconButton = React.memo(
    React.forwardRef<TOptionRef, TIconButtonProps>(function IconButton(props, ref): React.ReactElement {
        const { icon, size, loading, className, style, ...elementProps } = props;

        const inlineStyles: TIconButtonStyle = { ...style };

        if (size) {
            inlineStyles['--icon-button-size'] = `${size / 10}rem`;
        }

        return (
            <Option
                {...elementProps}
                flex="center"
                prefix={icon}
                loading={loading && 'prefix'}
                className={cn(styles.iconButton, className)}
                style={inlineStyles}
                data-size={size ? 'true' : undefined}
                data-ui-role="icon-button"
                ref={ref}
            />
        );
    }),
);

export default IconButton;

// #region Types

export type TIconButtonProps = Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'type' | 'prefix' | 'color' | 'children'
> & {
    elementAs?: OptionElement;
    icon: React.ReactElement;
    tooltip: NonNullable<TOptionProps['tooltip']>;
    loading?: boolean;
    submit?: boolean;
    danger?: boolean;
    noHoverStyle?: boolean;
    noFocusStyle?: boolean;
    propagate?: boolean;
    size?: number;
    delayClick?: NonNullable<TOptionProps['delayClick']>;
};

export type TIconButtonRef = TOptionRef;

export type TIconButtonStyle = React.CSSProperties & {
    '--icon-button-size'?: string;
};

// #endregion

// #region Examples

export const Example: TExample = {
    layout: 'column',
    render(props) {
        const style: React.CSSProperties = {
            display: 'flex',
            gap: '1em',
            width: '100%',
        };

        const tooltip = { placement: 'left', content: <span>Paint</span> } as const;

        return (
            <>
                <p style={style}>
                    <IconButton {...props} tooltip={tooltip} icon={<PaintIcon />} />
                    Default
                </p>
                <p style={style}>
                    <IconButton {...props} tooltip={tooltip} icon={<PaintIcon />} danger />
                    Danger
                </p>
                <p style={style}>
                    <IconButton {...props} tooltip={tooltip} icon={<PaintIcon />} loading />
                    Loading (Implicitly Disabled)
                </p>
                <p style={style}>
                    <IconButton {...props} tooltip={tooltip} icon={<PaintIcon />} loading disabled={false} />
                    Loading & Explicitly Enabled
                </p>
                <p style={style}>
                    <IconButton {...props} tooltip={tooltip} icon={<PaintIcon />} disabled />
                    Disabled
                </p>
            </>
        );
    },
    controls: {
        size: {
            type: 'number',
            initial: 40,
            connected: false,
        },
    },
    handlers: {
        onClick: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('IconButton', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
